import { useState } from "react";
import { Download, Loader2, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

/** Standardized CSV/XLSX export button reused across every admin list
 * (Donations, Guardian Donors, Performance donor list). Sends the caller's
 * current filters as query params so exports always match what's on screen. */
export default function ExportMenu({ url, params = {}, filename, testId = "export-menu" }) {
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await api.get(url, { params: { ...params, format }, responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `${filename}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast({ variant: "destructive", title: "Export failed" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={exporting} data-testid={testId}>
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport("csv")} data-testid={`${testId}-csv`}>
          <FileText className="h-4 w-4 mr-2" /> CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid={`${testId}-xlsx`}>
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
